import {
    FC,
    useEffect,
    useState,
    useRef,
    lazy,
    Suspense
} from 'react';
import { Step } from './form-wizard/types';
import { Form } from '../../components/ui';
import FormWizard from './form-wizard/FormWizard';
import useJurisdiction from '../../hooks/useJurisdiction';
import { RequestPublicForm } from './types';
import axios from 'axios';
import fingerprint from 'browser-fingerprint';
import useGoogleRecaptcha from '../../hooks/useGoogleRecaptcha';
import { useHistory, useParams } from 'react-router-dom';
import {
    LocationLoading,
    OrganizationLoading,
    InformationLoading
} from './loading-skeletons';
import useMounted from '../../hooks/useMounted';
import applyTheme from '../../utils/applyTheme';
import { ShowFormControlsProvider, useShowFormControls } from '../../contexts/show-form-controls-context';

const Location = lazy(() => import('./tabs/Location'));
const Organization = lazy(() => import('./tabs/Organization'));
const Information = lazy(() => import('./tabs/Information'));

type SortDictionary = {
    [key: string]: number;
};

const defaultSteps: Step[] = [
    {
        id: 'location',
        title: 'Location Of Issue',
        component: () => (
            <Suspense fallback={<LocationLoading />}>
                <Location />
            </Suspense>
        ),
    },
    {
        id: 'organization',
        title: 'Organization / Agency',
        component: () => (
            <Suspense fallback={<OrganizationLoading />}>
                <Organization />
            </Suspense>
        ),
    },
    {
        id: 'information',
        title: 'Information',
        component: () => (
            <Suspense fallback={<InformationLoading />}>
                <Information />
            </Suspense>
        ),
    },
];

const getDefaultSteps: (locationIssueTitle?: string) => Step[] = 
    (locationIssueTitle) => !!locationIssueTitle
        ? defaultSteps.map(step => 'location' === step.id ? {...step, title: locationIssueTitle} : step)
        : defaultSteps;

const Main: FC = () => {
    const history = useHistory();
    const { slug } = useParams<{ slug: string }>();
    
    const {
        address,
        addressLat,
        addressLng,
        user,
        requestSettings,
        organizations,
        code,
        requestMethod,
        recaptchaSiteKey,
        isNewRequestRecaptchaVerify,
        requestLocationIssueTitle,
        requestTheme,
        presetLocation,
        defaultServiceCode,
    } = useJurisdiction();
    
    const {
        isLoading,
        recaptcha
    } = useGoogleRecaptcha({
        key: recaptchaSiteKey
    });
    
    const [state] = useState<RequestPublicForm>({
        location_name: presetLocation && presetLocation.name ? presetLocation.name : '',
        location_phone: presetLocation && presetLocation.phone ? presetLocation.phone : '',
        address: presetLocation && presetLocation.address ? presetLocation.address : address,
        address2: presetLocation && presetLocation.address2 ? presetLocation.address2 : '',
        lat: presetLocation && presetLocation.latitude ? presetLocation.latitude : addressLat,
        lng: presetLocation && presetLocation.longitude ? presetLocation.longitude : addressLng,
        isNoLocation: false,
        serviceCode: defaultServiceCode ?? '',
        firstName: user?.name ?? '',
        lastName: '',
        email: presetLocation && presetLocation.email ? presetLocation.email :  (user?.email ?? ''),
        phone: user?.phone ?? '',
        description: '',
        isPrivate: false,
        isConfidential: false,
        isRequestAnonymous: false,
        serviceData: {},
        attachments: []
    });

    const [steps, setSteps] = useState<Step[]>(getDefaultSteps(requestLocationIssueTitle));
    
    const recaptchaKeyRef = useRef<string>('');
    
    const [showFormControls] = useShowFormControls();
    
    const handleSubmitRequest: (data: RequestPublicForm) => void = async (data) => {
        const params = {
            'jurisdiction_id': code,
            'service_code': data.serviceCode,
            // 'attribute': attributes,
            'lat': data.lat,
            'long': data.lng,
            'address': data.address,
            'address2': data.address2,
            'email': data.email,
            'description': data.description,
            'method': requestMethod,
            'subjuris_code': data.organization || '',
            'is_request_anonymous': data.isRequestAnonymous,
            'is_private': data.isPrivate,
            'is_confidential': data.isConfidential,
            'first_name': data.firstName,
            'last_name': data.lastName,
            'phone': data.phone,
            'token': recaptchaKeyRef.current,//google recaptcha
            'is_no_location': data.isNoLocation,
        };
        
        const formData = new FormData();
        
        Object.entries(params).forEach(([key, value]) => {
            formData.append(key, (value ?? '').toString());
        });
        
        Object.entries(data.serviceData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => {
                    formData.append(`attribute[${key}][]`, (value ?? '').toString());
                });
            }
            else {
                formData.append(`attribute[${key}]`, (value ?? '').toString());
            }
        });
        
        if (data.verificationPin) {
            formData.append('pin_code', data.verificationPin);
        }

        formData.append('fingerprint', fingerprint());

        if (data.attachments.length === 1) {
            formData.append('file', data.attachments[0], data.attachments[0].name);
        }
        else {
            data.attachments.forEach(attachment => {
                formData.append('file[]', attachment, attachment.name);
            });
        }

        try {
            const response = await axios.post(
                `https://www.ready311.com/open311/requests.xml?api_key=iframe&jurisdiction_id=${code}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            history.push(`/public/request/${slug}/success`);
        }
        catch ({ response: { data }}) {
            console.dir(data)
        }
    };
    
    useMounted(() => {
        applyTheme(requestTheme);
    });

    useEffect(() => {
        if (!recaptcha || !isNewRequestRecaptchaVerify) {
            return;
        }

        recaptcha.ready(() => {
            recaptcha
                .execute(recaptchaSiteKey, {action: 'homepage'})
                .then(token => {
                    recaptchaKeyRef.current = token;
                });
        });
        
    }, [recaptcha, isNewRequestRecaptchaVerify, recaptchaSiteKey]);
    
    useEffect(() => {
        const sort = requestSettings.tabsSort
            .reduce<SortDictionary>((acc, cur, index) => (acc[cur] = index, acc), {});

        const list = [ ...getDefaultSteps(requestLocationIssueTitle) ];
        // steps.sort((a, b) => sort[a.id] > sort[b.id] ? 1 : (sort[a.id] < sort[b.id] ? -1 : 0));
        list.sort((a, b) => sort[a.id] - sort[b.id]);

        setSteps([
            ...list
                .filter(step => requestSettings.enabledTabs.includes(step.id))
                .filter(step => step.id !== 'organization' || organizations.length !== 0)
        ])
    }, [requestSettings.tabsSort, requestSettings.enabledTabs, organizations]);

    return (
        <Form>
            <FormWizard
                steps={steps}
                defaultState={state}
                isShowNavigation={steps.length > 0}
                isShowNavigationControls={showFormControls}
                onFinalStepChange={handleSubmitRequest}
            />
        </Form>
    );
};

const WrappedMain = () => (
    <ShowFormControlsProvider>
        <Main />
    </ShowFormControlsProvider>
);

export default WrappedMain;
